/* Importing the necessary libraries and files */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as Three from 'three';
import { Joystick } from 'react-joystick-component';
import PropTypes from 'prop-types';

/* Importing the components from the material-ui library. */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Card,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  Stack,
  Step,
  Stepper,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import {
  Add as AddIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  East as EastIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowUp,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  KeyboardArrowDown,
  North as NorthIcon,
  Remove as RemoveIcon,
  Rotate90DegreesCcw as Rotate90DegreesCcwIcon,
  Rotate90DegreesCw as Rotate90DegreesCwIcon,
  South as SouthIcon,
  West as WestIcon,
} from '@mui/icons-material';

/* Importing the functions from the Redux slices. */
import { putDevice } from '../../features/device/deviceSlice';

/* Importing components. */
import TelepresenceAddMarker from '../../sections/device/telepresence.section.addMarker';
import TelepresenceDeleteMarker from '../../sections/device/telepresence.section.deleteMarker';
import myBESHIEIcon from './myBESHIEIcon.png';
import myBESHIELegend from './myBESHIELegend.png';

/* Importing the functions function from the utils folder. */
import handleFormResponse from '../../utils/handleFormResponse';
import { fDevice } from '../../utils/Device/Device.utils.format';
import { mDevice } from '../../utils/Device/Device.utils.map';

import { TELEPRESENCE_MARKER_COLORS } from '../../utils/Device/Device.utils.ValueSets';

TelepresenceConnection.propTypes = {
  device: PropTypes.object,
  setDevice: PropTypes.func,
  call: PropTypes.bool,
};

/**
 * Renders the TelepresenceConnection component.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.device - The device object.
 * @param {Function} props.setDevice - The function to set the device.
 * @returns {JSX.Element} The rendered TelepresenceConnection component.
 */
export default function TelepresenceConnection(props) {
  const { device, setDevice, call } = props;

  const dispatch = useDispatch();
  const mapContainerRef = useRef(null); // Reference to the map container

  const triangleStyle = {
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: '20px solid black', // Color of the triangle
  };

  /* Setting the initial state of the Patient object. */
  const [connected, setConnected] = useState(false);
  const [ros, setRos] = useState(null);
  const [NavClient, setNavClient] = useState(null);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [orientation, setOrientation] = useState(0);
  const [linearVelocity, setLinearVelocity] = useState(0);
  const [angularVelocity, setAngularVelocity] = useState(0);

  const topicName = 'planner/cmd_vel';

  const initROSConnection = useCallback(() => {
    const deviceURL = device.url;
    const Ros = new window.ROSLIB.Ros();

    Ros.on('connection', () => {
      console.log('connection established!');
      setConnected(true);
    });

    Ros.on('close', () => {
      console.log('connection closed!');
      setConnected(false);

      // try to reconnect
      setTimeout(() => {
        try {
          Ros.connect(deviceURL);
        } catch (error) {
          console.log('connection problem');
          console.log(error);
        }
      }, 5000);
    });

    try {
      Ros.connect(deviceURL);
    } catch (error) {
      console.log('connection problem');
      console.log(error);
    }
    setRos(Ros);
  }, [device.url]);

  const handleMove = (event) => {
    const cmdVel = new window.ROSLIB.Topic({
      ros,
      name: topicName,
      messageType: 'geometry_msgs/Twist',
    });

    const twist = new window.ROSLIB.Message({
      linear: {
        x: event.y,
        y: 0,
        z: 0,
      },
      angular: {
        x: 0,
        y: 0,
        z: -event.x,
      },
    });
    cmdVel.publish(twist);
  };

  const handleStop = () => {
    const cmdVel = new window.ROSLIB.Topic({
      ros,
      name: topicName,
      messageType: 'geometry_msgs/Twist',
    });

    const twist = new window.ROSLIB.Message({
      linear: {
        x: 0,
        y: 0,
        z: 0,
      },
      angular: {
        x: 0,
        y: 0,
        z: 0,
      },
    });

    cmdVel.publish(twist);
  };

  const getRobotState = useCallback(() => {
    if (ros) {
      const poseSubscriber = new window.ROSLIB.Topic({
        ros,
        // name: process.env.REACT_APP_POSE_TOPIC || 'amcl_pose',
        name: 'rtabmap/localization_pose',
        messageType: 'geometry_msgs/PoseWithCovarianceStamped',
      });

      const getOrientationFromQuaternion = (quaternion) => {
        const q = new Three.Quaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w);

        const RPY = new Three.Euler().setFromQuaternion(q);
        return RPY._z * (180 / Math.PI);
      };

      poseSubscriber.subscribe((message) => {
        setX(message.pose.pose.position.x);
        setY(message.pose.pose.position.y);
        setOrientation(getOrientationFromQuaternion(message.pose.pose.orientation));
      });

      const velocitySubscriber = new window.ROSLIB.Topic({
        ros,
        // name: process.env.REACT_APP_ODOM_TOPIC || 'odom',
        name: 't265/odom/sample',
        messageType: 'nav_msgs/Odometry',
      });

      velocitySubscriber.subscribe((message) => {
        setLinearVelocity(message.twist.twist.linear.x.toFixed(2));
        setAngularVelocity(message.twist.twist.angular.z.toFixed(2));
      });
    }
  }, [ros]);

  const [viewer, setViewer] = useState(null); // State for storing the viewer instance
  const [zoomView, setZoomView] = useState(null); // State for storing the zoomView instance
  const [panView, setPanView] = useState(null); // State for storing the panView instance

  useEffect(() => {
    if (viewer) {
      // Initialize ZoomView and PanView here after viewer is set
      const zView = new window.ROS2D.ZoomView({ rootObject: viewer.scene });
      const pView = new window.ROS2D.PanView({ rootObject: viewer.scene });

      const centerX = viewer.scene.canvas.width / 2;
      const centerY = viewer.scene.canvas.height / 2;

      pView.startPan(centerX, centerY);

      setZoomView(zView);
      setPanView(pView);

      // Store zoomView and panView in state or use them directly for pan and zoom functionalities
    }
  }, [viewer]);

  const updateViewerSize = useCallback(
    (newWidth, newHeight) => {
      // Check if the new size is significantly different from the current size
      const delta = 10; // Threshold for considering size changes significant
      if (
        viewer &&
        (Math.abs(viewer.scene.canvas.width - newWidth) > delta ||
          Math.abs(viewer.scene.canvas.height - newHeight) > delta)
      ) {
        // Update existing viewer size
        viewer.scene.canvas.width = newWidth;
        viewer.scene.canvas.height = newHeight;
      } else if (!viewer) {
        // Clear existing content in the div with id 'map'
        const mapDiv = document.getElementById('map');
        if (mapDiv) {
          while (mapDiv.firstChild) {
            mapDiv.removeChild(mapDiv.firstChild);
          }
        }

        // Create new viewer if it doesn't exist
        const newViewer = new window.ROS2D.Viewer({
          divID: 'map',
          width: newWidth,
          height: newHeight,
          background: '#FFFFFF',
        });
        // Setup the new viewer (e.g., navClient)
        setViewer(newViewer);
      }
    },
    [viewer]
  );

  useEffect(() => {
    const aspectRatio = call ? 16 / 9 : 4 / 3;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const { width } = entry.contentRect;
        const height = width / aspectRatio;
        updateViewerSize(width, height);
      });
    });

    const currentMapContainer = mapContainerRef.current;
    if (currentMapContainer) {
      resizeObserver.observe(currentMapContainer);
    }

    return () => {
      if (currentMapContainer) {
        resizeObserver.unobserve(currentMapContainer);
      }
    };
  }, [call, updateViewerSize]);

  const viewMap = useCallback(() => {
    if (ros && viewer) {
      const navClient = new window.NAV2D.OccupancyGridClientNav({
        ros,
        rootObject: viewer.scene,
        viewer,
        serverName: '/planner/move_base',
        withOrientation: true,
        image: myBESHIEIcon,
        topic: '/map', // messageType is 'nav_msgs/OccupancyGrid',
        continuous: true, // for continuous updates i.e., SLAM
        robot_pose: '/output_pose_nocovar', // originally /robot_pose and messageType is 'geometry_msgs/Pose'
      });
      setNavClient(navClient);
    }
  }, [ros, viewer]);

  const createPoseObject = (x, y, orientationDegree) => {
    // Convert degree to radian
    const orientationRadian = orientationDegree * (Math.PI / 180);

    // Convert the radian orientation into a quaternion.
    // Assuming the motion is mostly in the XY plane (i.e., roll = pitch = 0).
    const quaternion = new Three.Quaternion();
    quaternion.setFromEuler(new Three.Euler(0, 0, orientationRadian));

    const positionVec3 = new window.ROSLIB.Vector3({
      x,
      y,
      z: 0, // Assuming motion in the XY plane
    });

    const orientationQuat = new window.ROSLIB.Quaternion(quaternion);

    const pose = new window.ROSLIB.Pose({
      position: positionVec3,
      orientation: orientationQuat,
    });

    return pose;
  };

  const handleMarkerButtonClick = (marker) => {
    if (!markerButtonDisabled) {
      // Handle button click for the specific bed here
      const pose = createPoseObject(marker.positionX, marker.positionY, marker.orientation);
      NavClient.navigator.sendGoal(pose);
    }
  };

  const [activeStep, setActiveStep] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [markerButtonDisabled, setMarkerButtonDisabled] = useState(true);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const submitPutDevice = async (values) => {
    const data = { deviceID: device.id, data: values };
    const resultAction = await dispatch(putDevice(data));
    const payload = await handleFormResponse({
      resultAction,
      successDialog: false,
    });

    const updatedDevice = fDevice(payload);
    setDevice(updatedDevice);
  };

  const handleStepButtonClick = async (step) => {
    const formValues = {
      ...device,
    };

    formValues.mybeshieStep = step;
    const deviceData = mDevice(formValues);
    await submitPutDevice(deviceData);
  };

  const [markersPane, setMarkersPane] = useState('markers');
  const [viewMarkers, setViewMarkers] = useState(true);
  const [viewManualControl, setViewManualControl] = useState(false);

  const handleMarkersPaneChange = (event, newMarkersPane) => {
    setMarkersPane(newMarkersPane);
    if (newMarkersPane === 'manual-control') {
      setMarkersPane('manual-control');
      setViewManualControl(true);
      setViewMarkers(false);
    } else {
      setMarkersPane('markers');
      setViewManualControl(false);
      setViewMarkers(true);
    }
  };

  useEffect(() => {
    initROSConnection();
  }, [initROSConnection]);

  useEffect(() => {
    getRobotState();
    viewMap();
  }, [getRobotState, viewMap]);

  useEffect(() => {
    if (device) {
      if (device.mybeshieStep !== undefined) {
        setActiveStep(device.mybeshieStep);
      }
    }
  }, [device]);

  useEffect(() => {
    if (activeStep > 0) {
      setMarkerButtonDisabled(false);
    } else {
      setMarkerButtonDisabled(true);
    }
  }, [activeStep]);

  const convertMarkersToPoses = useCallback(() => {
    // Check if device.markers is an object
    if (device.markers && typeof device.markers === 'object') {
      return Object.keys(device.markers).map((key, index) => {
        const marker = device.markers[key];
        const pose = createPoseObject(marker.positionX, marker.positionY, marker.orientation);
        const color = TELEPRESENCE_MARKER_COLORS[index % TELEPRESENCE_MARKER_COLORS.length]; // Assigning colors in a cyclic manner

        return { pose, color }; // Return an object with pose and color properties
      });
    }
    return []; // Return an empty array if device.markers is undefined or null
  }, [device.markers]);

  function shouldUseWhiteText(backgroundColor) {
    // Function to determine if white text should be used based on background color
    // Extracting RGB values from the hex color string
    const r = parseInt(backgroundColor.slice(1, 3), 16);
    const g = parseInt(backgroundColor.slice(3, 5), 16);
    const b = parseInt(backgroundColor.slice(5, 7), 16);

    // Calculating brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  }

  useEffect(() => {
    if (NavClient) {
      const poses = convertMarkersToPoses();
      NavClient.navigator.createMarkers(poses);
    }
  }, [NavClient, convertMarkersToPoses, device.markers]);

  const handleZoomIn = () => {
    const centerX = viewer.scene.canvas.width / 2;
    const centerY = viewer.scene.canvas.height / 2;

    zoomView.startZoom(centerX, centerY);
    zoomView.zoom(1.1); // zoom in by 10%
  };

  const handleZoomOut = () => {
    const centerX = viewer.scene.canvas.width / 2;
    const centerY = viewer.scene.canvas.height / 2;

    zoomView.startZoom(centerX, centerY);
    zoomView.zoom(0.9); // zoom out by 10%
  };

  const panAmount = 10; // Adjust this value as needed

  const handlePan = (direction) => {
    let curX = panView.startPos.x;
    let curY = panView.startPos.y;

    switch (direction) {
      case 'left':
        curX -= panAmount;
        break;
      case 'right':
        curX += panAmount;
        break;
      case 'up':
        curY -= panAmount;
        break;
      case 'down':
        curY += panAmount;
        break;
      default:
        break;
    }

    panView.pan(curX, curY);
  };

  const rotateMap = (direction) => {
    const rotationAmount = 90;
    if (direction === 'clockwise') {
      viewer.scene.rotation += rotationAmount;
    } else if (direction === 'counter-clockwise') {
      viewer.scene.rotation -= rotationAmount;
    }
  };

  const handleCancelGoal = () => {
    if (NavClient && NavClient.navigator) {
      NavClient.navigator.cancelGoal();
    }
  };

  return (
    <>
      <Grid container rowSpacing={2} columnSpacing={4}>
        {activeStep < 2 && (
          <Grid container direction="row" justifyContent="center" alignItems="flex-start" item xs={12}>
            <Box sx={{ width: '80%', py: 3 }}>
              <Stepper activeStep={activeStep}>
                <Step>
                  <StepLabel>Train</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Test</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Finish</StepLabel>
                </Step>
              </Stepper>
            </Box>
          </Grid>
        )}

        <Grid container item xs={12}>
          {activeStep === 0 && (
            <Accordion
              expanded={expanded === 'panel1'}
              onChange={handleAccordionChange('panel1')}
              sx={{ boxShadow: 5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
                <Typography sx={{ width: '33%', flexShrink: 0 }}>Training Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <Typography>
                      1. Check the connection status of MyBESHIE to ensure it is properly connected.
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography>
                      2. Move MyBESHIE around the room to scan and map the entire area. Ensure that important features
                      of the room are visible in the scanned map section.
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography>3. To add a marker for navigation, follow these steps:</Typography>
                  </ListItem>
                  <List sx={{ pl: 4 }}>
                    <ListItem>
                      <Typography>
                        Position MyBESHIE precisely where you want to add the marker, ensuring correct orientation.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>Click the "Add Marker" button.</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>Enter a unique name for the marker.</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>
                        Remember to include a "Home" marker, which will serve as the default home base for MyBESHIE.
                      </Typography>
                    </ListItem>
                  </List>
                  <ListItem>
                    <Typography>4. To delete a marker, follow these steps:</Typography>
                  </ListItem>
                  <List sx={{ pl: 4 }}>
                    <ListItem>
                      <Typography>Click the "Delete Marker" button.</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>Select the marker you want to delete from the dropdown menu.</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>Click the "Delete" button and confirm your choice.</Typography>
                    </ListItem>
                  </List>
                  <ListItem>
                    <Typography>
                      5. Once you have completed mapping the room and added all desired markers, click the "Finish
                      Training" button.
                    </Typography>
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {activeStep === 1 && (
            <Accordion
              expanded={expanded === 'panel1'}
              onChange={handleAccordionChange('panel1')}
              sx={{ boxShadow: 5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
                <Typography sx={{ width: '33%', flexShrink: 0 }}>Testing Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <Typography>
                      1. After mapping, it's time to test the setup to ensure accuracy in position and orientation.
                      Check the connection status of MyBESHIE to ensure it is properly connected.
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography>2. Visual inspection of the room map:</Typography>
                  </ListItem>
                  <List sx={{ pl: 4 }}>
                    <ListItem>
                      <Typography>
                        Examine the map of the room and ensure that all important features are visible.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>
                        If any features are missing, you need to remap the room by returning to the "Training" step.
                        Click the "ReTrain" button to initiate the process.
                      </Typography>
                    </ListItem>
                  </List>
                  <ListItem>
                    <Typography>3. Testing individual markers:</Typography>
                  </ListItem>
                  <List sx={{ pl: 4 }}>
                    <ListItem>
                      <Typography>Click on each marker one at a time.</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>
                        Observe if MyBESHIE navigates to the desired position and orientation for the selected marker.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography>If MyBESHIE does not navigate correctly, follow these steps:</Typography>
                    </ListItem>
                    <List sx={{ pl: 8 }}>
                      <ListItem>
                        <Typography>Go back to the "Training" step.</Typography>
                      </ListItem>
                      <ListItem>
                        <Typography>Delete the problematic marker.</Typography>
                      </ListItem>
                      <ListItem>
                        <Typography>Position and orient MyBESHIE correctly.</Typography>
                      </ListItem>
                      <ListItem>
                        <Typography>Add a new marker for the corrected position and orientation.</Typography>
                      </ListItem>
                    </List>
                  </List>
                  <ListItem>
                    <Typography>
                      4. Once you have confirmed the correct position and orientation for each marker, the setup of
                      MyBESHIE is complete and ready for use.
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography>5. Click the "Finish Setup" button to proceed and start using MyBESHIE.</Typography>
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Grid>
        <Grid container justifyContent="space-between" item xs={12}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            <Stack direction="row" spacing={2}>
              <Chip label={`URL: ${device.url}`} color="primary" />
              <Chip
                label={`Status: ${connected ? 'Connected' : 'Disconnected'}`}
                color={connected ? 'success' : 'error'}
              />
            </Stack>
          </Stack>

          {activeStep === 2 && (
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={() => {
                handleStepButtonClick(0);
              }}
              startIcon={<ArrowBackIosNewIcon />}
            >
              ReTrain
            </Button>
          )}
        </Grid>

        <Grid container item xs={12} spacing={4}>
          <Grid container item xs={12} md={12} lg={call ? 12 : 9} xl={call ? 12 : 8}>
            <Grid container direction="row" justifyContent="center" alignItems="flex-start" item xs={12}>
              <Card variant="outlined" sx={{ width: '100%' }}>
                <div ref={mapContainerRef} style={{ width: '100%' }}>
                  <div id="map" />
                  <div style={{ position: 'absolute', top: '10px', left: '10px' }}>
                    <Stack direction="row" spacing={1}>
                      {/* Zoom Button Group */}
                      <ButtonGroup variant="contained">
                        <Tooltip title="Zoom In" placement="bottom">
                          <Button onClick={handleZoomIn} color="inherit" sx={{ py: 1 }}>
                            <AddIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Zoom Out" placement="bottom">
                          <Button onClick={handleZoomOut} color="inherit" sx={{ py: 1 }}>
                            <RemoveIcon />
                          </Button>
                        </Tooltip>
                      </ButtonGroup>

                      {/* Pan Button Group (Replace with actual pan handlers and icons) */}
                      <ButtonGroup variant="contained">
                        <Tooltip title="Pan Left" placement="bottom">
                          <Button onClick={() => handlePan('left')} color="inherit" sx={{ py: 1 }}>
                            <WestIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Pan Right" placement="bottom">
                          <Button onClick={() => handlePan('right')} color="inherit" sx={{ py: 1 }}>
                            <EastIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Pan Up" placement="bottom">
                          <Button onClick={() => handlePan('up')} color="inherit" sx={{ py: 1 }}>
                            <NorthIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Pan Down" placement="bottom">
                          <Button onClick={() => handlePan('down')} color="inherit" sx={{ py: 1 }}>
                            <SouthIcon />
                          </Button>
                        </Tooltip>
                      </ButtonGroup>

                      {/* Rotate Button Group (Replace with actual rotate handlers and icons) */}
                      <ButtonGroup variant="contained">
                        <Tooltip title="Rotate Clockwise" placement="bottom">
                          <Button onClick={() => rotateMap('clockwise')} color="inherit" sx={{ py: 1 }}>
                            <Rotate90DegreesCwIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Rotate Counter-Clockwise" placement="bottom">
                          <Button onClick={() => rotateMap('counter-clockwise')} color="inherit" sx={{ py: 1 }}>
                            <Rotate90DegreesCcwIcon />
                          </Button>
                        </Tooltip>
                      </ButtonGroup>
                      <Button variant="contained" color="error" onClick={handleCancelGoal} disabled={!connected}>
                        Cancel Goal
                      </Button>
                    </Stack>
                  </div>
                </div>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs="auto" display="flex" alignItems="center">
                      <Typography variant="h6">Map Legend</Typography>
                    </Grid>
                    <Grid item xs="auto" display="flex" alignItems="center">
                      <img
                        src={myBESHIELegend}
                        alt="myBESHIE"
                        style={{ width: '40px', height: '40px', marginRight: '10px' }}
                      />
                      <Typography>myBESHIE</Typography>
                    </Grid>
                    <Grid item xs display="flex" alignItems="center">
                      <div style={triangleStyle} />
                      <Typography style={{ marginLeft: '10px' }}>Marker (Tip indicates orientation)</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Grid>
          </Grid>
          <Grid
            justifyContent="center"
            alignItems="flex-start"
            item
            xs={12}
            md={12}
            lg={call ? 12 : 3}
            xl={call ? 12 : 4}
          >
            {activeStep !== 2 && (
              <Grid item xs={12} md={12}>
                <Typography variant="h6">Markers</Typography>
              </Grid>
            )}
            {activeStep === 2 && (
              <ToggleButtonGroup
                color="primary"
                size="small"
                value={markersPane}
                exclusive
                onChange={handleMarkersPaneChange}
                sx={{ pb: 2 }}
              >
                <ToggleButton value="markers">Markers</ToggleButton>
                <ToggleButton value="manual-control">Manual Control</ToggleButton>
              </ToggleButtonGroup>
            )}
            {activeStep === 0 && (
              <Grid item xs={12} md={12}>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ py: 3 }}>
                  <TelepresenceAddMarker
                    device={device}
                    setDevice={setDevice}
                    positionX={x}
                    positionY={y}
                    orientation={orientation}
                  />
                  <TelepresenceDeleteMarker device={device} setDevice={setDevice} />
                </Stack>
              </Grid>
            )}

            {device.markers && (activeStep < 2 || (activeStep === 2 && viewMarkers)) && (
              <Grid container item xs={12} md={12} lg={12} spacing={2}>
                {activeStep > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Select a marker to navigate to:</Typography>
                  </Grid>
                )}
                {Object.keys(device.markers).map((marker, index) => {
                  const markerColor = TELEPRESENCE_MARKER_COLORS[index % TELEPRESENCE_MARKER_COLORS.length]; // Get color based on index
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={6} key={marker}>
                      <Button
                        variant={activeStep > 0 ? 'contained' : 'outlined'}
                        disableFocusRipple={markerButtonDisabled}
                        disableRipple={markerButtonDisabled}
                        fullWidth
                        onClick={() => handleMarkerButtonClick(device.markers[marker])}
                        style={{
                          backgroundColor: markerColor, // Applying the color to the button
                          color: shouldUseWhiteText(markerColor) ? '#FFF' : '#000', // Adjusting text color for visibility
                        }}
                      >
                        {marker}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {activeStep === 2 && viewManualControl && (
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Click and drag the circle to move MyBESHIE:</Typography>
                </Grid>
                {/* Manual Control Joystick */}
                <Grid item xs={12} md={6} lg={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} container justifyContent="center">
                      <KeyboardArrowUp />
                    </Grid>
                    <Grid item xs={12} container justifyContent="center" alignItems="center">
                      <Grid item xs>
                        <Grid container justifyContent="flex-end">
                          <KeyboardArrowLeft />
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Joystick
                          size={100}
                          baseColor={blue[50]}
                          stickColor={blue[500]}
                          move={handleMove}
                          stop={handleStop}
                          disabled={!connected}
                        />
                      </Grid>
                      <Grid item xs>
                        <Grid container justifyContent="flex-start">
                          <KeyboardArrowRight />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} container justifyContent="center">
                      <KeyboardArrowDown />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Position and Velocity Information */}
                <Grid item xs={12} md={6} lg={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">Position</Typography>
                      <Typography variant="body1">x: {x.toFixed(2)}</Typography>
                      <Typography variant="body1">y: {y.toFixed(2)}</Typography>
                      <Typography variant="body1">Orientation: {orientation.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">Velocities</Typography>
                      <Typography variant="body1">Linear Velocity: {linearVelocity}</Typography>
                      <Typography variant="body1">Angular Velocity: {angularVelocity}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid container direction="row-reverse" justifyContent="space-between" alignItems="flex-start" item xs={12}>
          {activeStep === 0 && (
            <Button
              variant="contained"
              size="large"
              color="success"
              onClick={() => {
                handleStepButtonClick(1);
              }}
              endIcon={<ArrowForwardIosIcon />}
            >
              Finish Training
            </Button>
          )}
          {activeStep === 1 && (
            <>
              <Button
                variant="contained"
                size="large"
                color="success"
                onClick={() => {
                  handleStepButtonClick(2);
                }}
                endIcon={<ArrowForwardIosIcon />}
              >
                Finish Setup
              </Button>
              <Button
                variant="contained"
                size="large"
                color="warning"
                onClick={() => {
                  handleStepButtonClick(0);
                }}
                startIcon={<ArrowBackIosNewIcon />}
              >
                ReTrain
              </Button>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
}
